import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  handleError,
  handlePending,
  PageState,
  ThunkApi
} from 'lib/utils/redux';
import moment, { Moment } from 'moment';

import { Inventory, PatientSubcollection } from 'lib/documents';
import { getTimestampedDocId } from 'lib/utils/object';

import pcsJson from 'data/pcs.json';
import { QuestionConfigWithResponse } from 'features/patient/assessment/_components/questionnaire-page';
import { savePendingInventory } from 'features/patient/assessment/assessment-controller/questionnaire-control-flow-slice';
import { PcsCategory, pcsPercentile } from 'lib/documents/pcs';
import { z } from 'zod';
import { PostExecutionCallbacks } from '../../_components/questionnaire-paginator-types';
import { controlflowSubmissionEventHandler } from '../../assessment-controller/_utils/control-flow-submission-eventhandler';

export interface PcsInventory extends Inventory {
  /**
   * @deprecated
   */
  description: string;
  /**
   * @deprecated
   */
  instructions: string;
  /**
   * @deprecated
   */
  min: number;
  /**
   * @deprecated
   */
  max: number;
  /**
   * @deprecated
   */
  beforeLabel: string;
  /**
   * @deprecated
   */
  afterLabel: string;
  /**
   * @deprecated
   */
  colorsForward: boolean;
  /**
   * @deprecated
   */
  iconsForward: boolean;
}

export interface PcsState extends PageState {
  pcs: PcsInventory;
  invalidItems: string[];
  isSubmitting: boolean;
  lastUpdated?: Moment;
  showModal: boolean;
}

export const min = 0;
export const max = 6;
export const NOT_SELECTED = -1;
export const pcsOrder = pcsJson.questions.map(q => q.title);
// 'Please rate these questions from 0 (never) to 4 (always)',
//       'Everyone experiences painful situations at some point in their lives. Such experiences may include headaches, tooth pain, joint or muscle pain. People are often exposed to situations that may cause pain such as illness, injury, dental procedures or surgery. We are interested in the types of thoughts and feelings that you have when you are in pain. Listed below are the thirteen statements describing different thoughts and feelings that may be associated with pain. Using the following scale, please indicate the degree to which you have these thoughts and feelings when you are experiencing pain.',
function createNewPcs(): PcsInventory {
  return {
    id: 'unknown',
    date: moment(),
    data: pcsJson.questions.reduce((acc: Record<string, number>, question) => {
      acc[question.title] = NOT_SELECTED;
      return acc;
    }, {}),
    description: pcsJson.description,
    instructions: pcsJson.instructions,
    min: pcsJson.min,
    max: pcsJson.max,
    beforeLabel: pcsJson?.beforeLabel ? pcsJson.beforeLabel : 'Never',
    afterLabel: pcsJson?.afterLabel ? pcsJson.afterLabel : 'Always',
    colorsForward: pcsJson.colorsForward ?? true,
    iconsForward: pcsJson.iconsForward ?? true
  };
}
export const PCS_CATEGORIES = createCategories();
function createCategories(): Record<string, string[]> {
  return pcsJson.questions.reduce((acc: Record<string, string[]>, question) => {
    acc[question.title] = question.categories;
    return acc;
  }, {});
}

const initialState: PcsState = {
  hasError: false,
  pcs: createNewPcs(),
  invalidItems: [],
  isLoading: false,
  isSubmitting: false,
  showModal: false
};

/**
 * @deprecated Remove during the next refactor
 */
const initialLoad = createAsyncThunk('pcs/initialLoad', async () => {
  return { date: undefined };
});

const submit = createAsyncThunk<
  boolean,
  PostExecutionCallbacks,
  ThunkApi<PcsState>
>('pcs/submit', async (callbacks, { getState, dispatch }) => {
  return await controlflowSubmissionEventHandler(callbacks, async () => {
    dispatch(pcsSlice.actions.validate());

    const { invalidItems } = getState();
    if (invalidItems.length) return false;

    dispatch(pcsSlice.actions.setPcsDate());
    dispatch(pcsSlice.actions.calculateCategories());

    const { pcs } = getState();

    await savePendingInventory(PatientSubcollection.InventoriesPcs, pcs);
    return true;
  });
});

const pcsSlice = createSlice({
  name: 'pcs',
  initialState,
  reducers: {
    setPcsDate: state => {
      const now = moment();
      state.pcs.id = getTimestampedDocId(now);
      state.pcs.date = now;
    },
    updatePcsItem: (
      state,
      {
        payload
      }: PayloadAction<Pick<QuestionConfigWithResponse, 'title' | 'value'>>
    ) => {
      const { title, value } = payload;
      state.invalidItems = state.invalidItems.filter(i => i !== title);
      state.pcs.data[title] = value;
    },
    validate: state => {
      state.invalidItems = Object.entries(state.pcs.data)
        .filter(([, value]) => value === NOT_SELECTED)
        .map(([key]) => key);
    },
    closeModal: state => {
      state.showModal = false;
    },
    calculateCategories: state => {
      for (const category of Object.values(PcsCategory)) {
        state.pcs.data[category] = 0;
      }

      Object.entries(state.pcs.data).forEach(([title, value]) => {
        if (PCS_CATEGORIES[title]) {
          for (const category of PCS_CATEGORIES[title]) {
            if (isNaN(Number(state.pcs.data[category]))) {
              throw new Error(
                `[pcsSlice][calculateCategories] Category '${category}' is not a number. Got: '${JSON.stringify(
                  state.pcs.data[category]
                )}'`
              );
            }
            (state.pcs.data[category] as number) += z.number().parse(value);
          }
        }
      });

      // add category for perecentile we don't really need this.
      state.pcs.data['percentile'] = 0;

      // get percentile based on Total score
      state.pcs.data['percentile'] = pcsPercentile(
        z.number().parse(state.pcs.data['Total score']),
        PcsCategory.TOTAL
      );
    }
  },
  extraReducers: builder => {
    builder.addCase(initialLoad.pending, handlePending);
    builder.addCase(initialLoad.rejected, handleError);
    builder.addCase(initialLoad.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.lastUpdated = payload ? payload.date : undefined;
    });
    builder.addCase(submit.pending, state => {
      state.isSubmitting = true;
    });
    builder.addCase(submit.rejected, handleError);
    builder.addCase(submit.fulfilled, (state, { payload: isSaved }) => {
      state.isSubmitting = false;
      if (isSaved) {
        state.showModal = isSaved;
        state.lastUpdated = moment().utc(true);
        state.pcs = createNewPcs();
      }
    });
  }
});

export { initialLoad, initialState, submit };
export const { setPcsDate, updatePcsItem, closeModal, validate } =
  pcsSlice.actions;
export default pcsSlice.reducer;
export const PcsSliceReducer = pcsSlice.reducer;
